import { Injectable } from '@angular/core';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { QueryParamsModel } from '../../_base/crud';
import { AppState } from '../../../core/reducers';

import { Router } from '@angular/router';
import {
  AffiliationTypeActionTypes,
  AffiliationTypeCreated,
  AffiliationTypeDeleted,
  AffiliationTypeOnServerCreated,
  AffiliationTypeOnServerDeleted,
  AffiliationTypeOnServerUpdated,
  AffiliationTypesActionToggleLoading,
  AffiliationTypesPageCancelled,
  AffiliationTypesPageLoaded,
  AffiliationTypesPageRequested,
  AffiliationTypesPageToggleLoading,
  AffiliationTypeUpdated,
  ClearAffiliationTypeErrors,
  ThrowAffiliationTypeError,
} from '../_actions/affiliation-type.actions';
import { AffiliationsService } from '../_services';

@Injectable()
export class AffiliationTypeEffects {
  showPageLoadingDistpatcher = new AffiliationTypesPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new AffiliationTypesPageToggleLoading({ isLoading: false });
  showActionLoadingDistpatcher = new AffiliationTypesActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new AffiliationTypesActionToggleLoading({ isLoading: false });

  @Effect()
  loadAffiliationTypesPage$ = this.actions$.pipe(
    ofType<AffiliationTypesPageRequested>(AffiliationTypeActionTypes.AffiliationTypesPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.affiliationType.findAffiliationTypes(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin(requestToServer, lastQuery).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new AffiliationTypesPageCancelled();
            } else {
              this.store.dispatch(new ThrowAffiliationTypeError({ error: response[0].error }));
              return new AffiliationTypesPageCancelled();
            }
          } else {
            // tslint:disable-next-line:no-shadowed-variable
            const lastQuery: QueryParamsModel = response[1];
            this.store.dispatch(new ClearAffiliationTypeErrors());
            return new AffiliationTypesPageLoaded({
              affiliationTypes: response[0].data,
              totalCount: response[0].count,
              page: lastQuery,
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deleteAffiliationType$ = this.actions$.pipe(
    ofType<AffiliationTypeOnServerDeleted>(
      AffiliationTypeActionTypes.AffiliationTypeOnServerDeleted,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.affiliationType.deleteAffiliationType(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map(() => {
          this.store.dispatch(this.hideActionLoadingDistpatcher);
          return new AffiliationTypeDeleted(payload);
        }),
      );
    }),
  );

  @Effect()
  updateAffiliationType$ = this.actions$.pipe(
    ofType<AffiliationTypeOnServerUpdated>(
      AffiliationTypeActionTypes.AffiliationTypeOnServerUpdated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.affiliationType.updateAffiliationType(payload.affiliationType).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowAffiliationTypeError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(new ClearAffiliationTypeErrors());
            return new AffiliationTypeUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createAffiliationType$ = this.actions$.pipe(
    ofType<AffiliationTypeOnServerCreated>(
      AffiliationTypeActionTypes.AffiliationTypeOnServerCreated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.affiliationType.createAffiliationType(payload.affiliationType).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res.status === 201) {
            this.store.dispatch(new ClearAffiliationTypeErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new AffiliationTypeCreated({ affiliationType: res });
          } else {
            return new ThrowAffiliationTypeError({ error: res });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private affiliationType: AffiliationsService,
    private store: Store<AppState>,
    private router: Router,
  ) {}
}
